/**
 * Emissions Insights Page
 *
 * Analytics-style page with sidebar navigation for different insight dashboards.
 * View-first approach: user picks the analytical view, then selects entity type.
 *
 * Asset views were promoted to a top-level "Assets" tab — see
 * `EmissionsAssetsListPage` / `EmissionsAssetDashboardPage`.
 */

import { useCallback, useMemo } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { TrendingUp, Gauge, Users, BarChart3, Grid3x3, Sparkles, Truck, Flame } from 'lucide-react';
import { DashboardWithSidebar, SIDEBAR_HIDE_AT } from '@g17eco/ui-modern/components/ui/dashboard';
import { cn } from '@g17eco/ui-modern/lib/utils';
import { TabNav, type TabItem } from '@g17eco/ui-modern/components/ui/tab-nav';
import { TrendsDashboard } from '../components/insights/TrendsDashboard';
import { ComparisonDashboard } from '../components/insights/ComparisonDashboard';
import { HeatmapDashboard } from '../components/insights/HeatmapDashboard';
import { IntensityDashboard } from '../components/insights/IntensityDashboard';
import { SourcesDashboard } from '../components/insights/SourcesDashboard';
import { IntelligenceDashboard } from '../components/insights/IntelligenceDashboard';
import { InsightsSidebar } from '../components/insights/InsightsSidebar';
import { useGetEmissionsSummaryQuery, useGetIntensityMetricsQuery } from '../api/emissions';
import {
  FiscalYearSelector,
  getFiscalYearPeriod,
  getPreviousYearPeriod,
} from '../components/FiscalYearSelector';
import { IncludeChildrenToggle } from '../components/IncludeChildrenToggle';
import { useEmissionsFilters } from '../hooks/useEmissionsFilters';

type InsightsTab = 'trends' | 'sources' | 'comparison' | 'heatmap' | 'intensity' | 'employees' | 'suppliers' | 'intelligence';

const tabs: TabItem<InsightsTab>[] = [
  { key: 'trends', label: 'Trends', icon: <TrendingUp className='tw:size-4' /> },
  { key: 'sources', label: 'Sources', icon: <Flame className='tw:size-4' /> },
  { key: 'comparison', label: 'Comparison', icon: <BarChart3 className='tw:size-4' /> },
  { key: 'heatmap', label: 'Heatmap', icon: <Grid3x3 className='tw:size-4' /> },
  { key: 'intensity', label: 'Intensity', icon: <Gauge className='tw:size-4' /> },
  { key: 'employees', label: 'Employees', icon: <Users className='tw:size-4' />, disabled: true },
  { key: 'suppliers', label: 'Suppliers', icon: <Truck className='tw:size-4' />, disabled: true },
  { key: 'intelligence', label: 'Intelligence', icon: <Sparkles className='tw:size-4' />, disabled: true },
];

const COMING_SOON_TABS = new Set<InsightsTab>(
  tabs.filter((t) => t.disabled).map((t) => t.key),
);

const mobileTabs: TabItem<InsightsTab>[] = tabs.map(({ icon: _icon, ...rest }) => rest);

const TAB_KEYS = new Set<string>(tabs.map((t) => t.key));

const getTabFromSearch = (search: string): InsightsTab => {
  const params = new URLSearchParams(search);
  const tab = params.get('tab');
  return tab && TAB_KEYS.has(tab) ? (tab as InsightsTab) : 'trends';
};

export const EmissionsInsightsPage = () => {
  const { initiativeId = '' } = useParams<{ initiativeId: string }>();
  const location = useLocation();
  const history = useHistory();
  const { hasChildren, selectedFiscalYear, setSelectedFiscalYear, availableYears, includeChildren, setIncludeChildren } = useEmissionsFilters();

  const activeTab = getTabFromSearch(location.search);
  const setActiveTab = useCallback((tab: InsightsTab) => {
    const params = new URLSearchParams(location.search);
    if (tab === 'trends') {
      params.delete('tab');
    } else {
      params.set('tab', tab);
    }
    const search = params.toString();
    history.replace({ pathname: location.pathname, search: search ? `?${search}` : '' });
  }, [location.search, location.pathname, history]);

  const periodDates = useMemo(() => getFiscalYearPeriod(selectedFiscalYear), [selectedFiscalYear]);
  const previousPeriodDates = useMemo(
    () => getPreviousYearPeriod(selectedFiscalYear),
    [selectedFiscalYear]
  );

  const allTimePeriod = useMemo(() => {
    if (!availableYears || availableYears.length === 0) return undefined;
    const minYear = Math.min(...availableYears);
    const maxYear = Math.max(...availableYears);
    return { start: `${minYear}-01-01`, end: `${maxYear}-12-31` };
  }, [availableYears]);

  const {
    data: summary,
    isLoading: summaryLoading,
  } = useGetEmissionsSummaryQuery(
    { initiativeId, periodStart: periodDates.start, periodEnd: periodDates.end, includeChildren },
    { skip: !initiativeId }
  );

  const {
    data: previousYearSummary,
    isLoading: previousYearLoading,
  } = useGetEmissionsSummaryQuery(
    { initiativeId, periodStart: previousPeriodDates.start, periodEnd: previousPeriodDates.end, includeChildren },
    { skip: !initiativeId }
  );

  const {
    data: intensityMetrics,
    isLoading: intensityLoading,
  } = useGetIntensityMetricsQuery(
    { initiativeId, periodStart: periodDates.start, periodEnd: periodDates.end, includeChildren },
    { skip: !initiativeId }
  );

  return (
    <DashboardWithSidebar
      sidebar={<InsightsSidebar items={tabs} activeItem={activeTab} onChange={setActiveTab} />}
    >
      {/* Page Header */}
      <div className='tw:flex tw:justify-between tw:items-center tw:mb-6'>
        <div>
          <h4 className='tw:text-xl tw:font-semibold tw:mb-1'>Analytics & Insights</h4>
          <p className='tw:text-sm tw:text-muted-foreground'>
            Deep dive into your emissions data and trends
          </p>
        </div>
        <div className='tw:flex tw:gap-2'>
          <IncludeChildrenToggle disabled={!hasChildren} includeChildren={includeChildren} onChange={setIncludeChildren} />
          <FiscalYearSelector
            selectedYear={selectedFiscalYear}
            onYearChange={setSelectedFiscalYear}
            years={availableYears}
          />
        </div>
      </div>

      {/* Mobile Tab Navigation (hidden on xxl+ where sidebar is visible) */}
      <div className={cn('tw:block tw:mb-6', SIDEBAR_HIDE_AT['md'])}>
        <TabNav variant='links' fullWidth tabs={mobileTabs} activeTab={activeTab} onChange={setActiveTab} />
      </div>

      {/* Dashboard Content */}
      <div className='tw:mb-6'>
        {activeTab === 'trends' && (
          <TrendsDashboard
            initiativeId={initiativeId}
            includeChildren={includeChildren}
            summary={summary}
            previousSummary={previousYearSummary}
            intensityMetrics={intensityMetrics}
            summaryLoading={summaryLoading}
            previousLoading={previousYearLoading}
            intensityLoading={intensityLoading}
            allTimePeriodStart={allTimePeriod?.start}
            allTimePeriodEnd={allTimePeriod?.end}
            availableYears={availableYears}
            periodStart={periodDates.start}
            periodEnd={periodDates.end}
            selectedFiscalYear={selectedFiscalYear}
          />
        )}
        {activeTab === 'sources' && (
          <SourcesDashboard
            initiativeId={initiativeId}
            periodStart={periodDates.start}
            periodEnd={periodDates.end}
            includeChildren={includeChildren}
          />
        )}
        {activeTab === 'comparison' && (
          <ComparisonDashboard
            initiativeId={initiativeId}
            periodStart={periodDates.start}
            periodEnd={periodDates.end}
            includeChildren={includeChildren}
          />
        )}
        {activeTab === 'heatmap' && (
          <HeatmapDashboard
            initiativeId={initiativeId}
            periodStart={periodDates.start}
            periodEnd={periodDates.end}
            includeChildren={includeChildren}
          />
        )}
        {activeTab === 'intensity' && (
          <IntensityDashboard
            initiativeId={initiativeId}
            periodStart={periodDates.start}
            periodEnd={periodDates.end}
            includeChildren={includeChildren}
          />
        )}
        {activeTab === 'intelligence' && (
          <IntelligenceDashboard isLoading={summaryLoading} />
        )}
        {COMING_SOON_TABS.has(activeTab) && (
          <div className='tw:flex tw:flex-col tw:items-center tw:justify-center tw:py-16 tw:text-center'>
            <div className='tw:rounded-full tw:bg-muted tw:p-4 tw:mb-4'>
              {tabs.find((t) => t.key === activeTab)?.icon}
            </div>
            <h5 className='tw:font-semibold tw:mb-2'>
              {tabs.find((t) => t.key === activeTab)?.label}
            </h5>
            <p className='tw:text-sm tw:text-muted-foreground tw:max-w-md'>
              This section is coming soon. Stay tuned for detailed analytics and insights.
            </p>
            <span className='tw:text-xs tw:bg-muted tw:text-muted-foreground tw:px-3 tw:py-1 tw:rounded-full tw:mt-2'>
              Coming Soon
            </span>
          </div>
        )}
      </div>
    </DashboardWithSidebar>
  );
};
